/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useFormik } from 'formik';
import { RcFile } from 'antd/es/upload';
import { Flex } from 'antd';
import dayjs from 'dayjs';

import {
  usePostITRequestFormMutation,
  useSubmitRequestMutation,
  useValidateCugMobileMutation,
} from '@Features/EmployeeSelfService/requests/ITRequisition/store/api/itRequisitionApiSlice';
import { useFormUploadMutation } from '@Store/api/dmsApiSlice';
import {
  useAppDispatch,
  useAppSelector,
  useHasAccessToSubmit,
  useNavigationToListRequests,
} from '@Hooks/index';
import {
  getItRequisitionTypeOptions,
  itReqCreateRequisitionInfo,
} from '@Features/EmployeeSelfService/requests/ITRequisition/store/selectors/itRequisitionSelector';
import { itRequisitionActions } from '@Features/EmployeeSelfService/requests/ITRequisition/store/ITRequisitionSlice';
import { RcCustomRequestOptions } from '@Components/types/UploadType';
import { SimCapCodes } from '@Enum/api/SimCapCodes.enum';
import {
  getITAccessories,
  getOrganizationOptions,
  getSIMCapValueDictSelector,
} from '@Store/selectors/dictionarySelector';
import { currentFileUploadSelector } from '@Store/selectors/fileUploadSelector';
import {
  currentSelectedUserSelector,
  currentUserSelector,
} from '@Store/selectors/userInfoSelector';
import {
  DocumentFormUploadResponse,
  ITRequestFormRequest,
  RequestError,
} from '@Types/api/form';
import { ItRequisitionTypeCodes } from '@Enum/api/ItRequisitionTypeCodes.enum';
import {
  transformDictToOptions,
  transformDictToOptionsNum,
  transformProjectsToOptions,
} from '@Utils/transformToOptions';
import { splitUserName } from '@Utils/splitUserName';
import { useGetUserInfoMutation } from '@Store/api/userApiSlice';
import { userInfoActions } from '@Store/slices/userInfoSlice';
import { fileUploadActions } from '@Store/slices/fileUploadSlice';
import { generateFilepath } from '@Utils/generateFilepath';
import { NotificationContext } from '@Context/NotificationContext';
import { validateForm } from '@Features/utils/validateForm';
import { FooterActions } from '@Enum/index';
import { MainForm } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { useGetProjectsForOrgMutation } from '@Store/api/dictionaryApiSlice';
import { Option } from '@Components/types/options';
import { ModuleOptions } from '@Features/EmployeeSelfService/requests/ITRequisition/fieldsOptions';
import { isMobileNumberValid } from '@Utils/isMobileNumberValid';
import { User } from '@Types/index';

import { ITRequestFormValues } from '../type';
import RequestDetailsSection from '../components/RequestDetailsSection/RequestDetailsSection';
import { initialValuesForm } from '../initialValues';
import { getFormUploadFileMetaData } from '../utils/document';
import { getItRequisitionFormSchema } from '../validationSchema';
import {
  getAccessoriesValue,
  getPersonalMobileValue,
  getUsernameValue,
  getValidationCugError,
} from '../utils';

const descriptionAlert = (
  <div>
    Note:
    <ol type="1">
      <li>
        Any Unofficial international calls will be deducted from the
        Employee(Requester have to advise the Employee while handing over the
        SIM card)
      </li>
      <li>For SIM Card: 0 (Default) means unlimited CUG only</li>
      <li>
        Kindly discuss with your project/department manager for SIM card credit
        limit other than 0 (Default)
      </li>
      <li>
        If Current Location is wrong in Employee Basic information, Kindly
        inform to the Payroll/HR Assistant before creating the request *
      </li>
      <li>
        Kindly discuss with employee and select the desired email ID ! Before
        creating request *
      </li>
    </ol>
  </div>
);

const ITRequestCreateForm: React.FC = () => {
  const {
    saveSuccessNotification,
    errorNotification,
    submitSuccessNotification,
  } = useContext(NotificationContext);
  const dispatch = useAppDispatch();
  const [getUser] = useGetUserInfoMutation();
  const organizations = useAppSelector(getOrganizationOptions);
  const currentUser = useAppSelector(currentUserSelector);
  const selectedUser = useAppSelector(currentSelectedUserSelector);
  const currentFileUpload = useAppSelector(currentFileUploadSelector);
  const itReqTypeOptions = useAppSelector(getItRequisitionTypeOptions);
  const simCapValues = useAppSelector(getSIMCapValueDictSelector);
  const itAccessories = useAppSelector(getITAccessories);
  const requisitionInfo = useAppSelector(itReqCreateRequisitionInfo);
  const [postITRequestForm] = usePostITRequestFormMutation();
  const [submitForm] = useSubmitRequestMutation();
  const [formUpload] = useFormUploadMutation();
  const [validateCugMobile] = useValidateCugMobileMutation();

  const [projects, setProjects] = useState<Option[]>([]);
  const [disabledSaveForm, setDisableSaveForm] = useState(false);
  const [splittedUserName, setSplittedUserName] = useState<Array<string>>(['']);
  const [initialFormValues, setInitialFormValues] = useState(initialValuesForm);
  const [itReqType, setItReqType] = useState<number | null>(null);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [modalContext, setModalContext] = useState<null | 'save' | 'submit'>(
    null
  );

  const { handleBackToListRequests } = useNavigationToListRequests();
  const [getProjects] = useGetProjectsForOrgMutation();
  const [selectedEmpUser, setSelectedEmpUser] = useState<User>();
  const hasAccessToSubmit = useHasAccessToSubmit('ITRequisition');

  useLayoutEffect(() => {
    if (!hasAccessToSubmit) {
      setDisableSaveForm(true);
    }
  }, [hasAccessToSubmit]);

  const chosenUser = useMemo(() => {
    if (selectedUser?.empId) {
      return selectedUser;
    }

    return currentUser;
  }, [currentUser, selectedUser]);

  useEffect(
    () => () => {
      dispatch(itRequisitionActions.setITRequisition(null));
      dispatch(fileUploadActions.setFile(null));
      dispatch(userInfoActions.setSelectedUser(null));
    },
    []
  );

  useEffect(() => {
    if (!simCapValues.length) {
      return;
    }

    setInitialFormValues({
      ...initialValuesForm,
      simCardCreditLimit: SimCapCodes.ZERO_DEFAULT,
    });
  }, [simCapValues]);

  const handleFileRemove = useCallback((): void => {
    dispatch(fileUploadActions.setFile(null));
  }, []);

  const handleFileRequest = useCallback(
    (options: RcCustomRequestOptions): void => {
      const { file, onSuccess, onError } = options;
      const cb = (): void => {};
      const successCb = onSuccess || cb;
      const errorCb = onError || cb;
      const formData = new FormData();
      formData.append('file', file);

      if (file) {
        const filepath = generateFilepath('ITReq', (file as RcFile).name);
        dispatch(
          fileUploadActions.setFile({
            uploadedFile: file as RcFile,
            formData,
            filepath,
          })
        );
        successCb('Ok');
      } else {
        errorCb(new Error('file not uploaded'));
      }
    },
    []
  );

  const uploadFile = useCallback(
    (reqFileId: string): Promise<DocumentFormUploadResponse> => {
      const { formData, filepath } = currentFileUpload;

      const body = getFormUploadFileMetaData(reqFileId, filepath);

      return formUpload({
        document: JSON.stringify(body),
        body: formData,
      }).unwrap();
    },
    [currentFileUpload]
  );

  const handleSubmitForm = useCallback(
    (
      values: ITRequestFormValues,
      formikHelpers: any,
      isModalAccepted = false
    ) => {
      const submitFormHandler = async (): Promise<void> => {
        const intCallNumber = `+${values.countryCode} ${values.intCallsNumber}`;
        const personalMobileValue = getPersonalMobileValue(
          values.itReqType,
          intCallNumber,
          values.contactNo
        );

        const usernameValue = getUsernameValue(
          values.itReqType,
          values.emailIdLeft,
          values.emailIdRight,
          chosenUser.empName
        );
        const accessoriesValue = getAccessoriesValue(
          values.itReqType,
          values.emailSignatureLeft,
          values.emailSignatureRight,
          values.accessories,
          itAccessories
        );

        let body = {
          reqItemId: values.itReqType as number,
          reqCapValue: values.simCardCreditLimit as number,
          isNewUser: values.isNewUser as number,
          modules: values.module,
          acctModules: values.accountsModule,
          organizations: [values.organization || ''],
          projects: values.projects,
          role: values.requiredRole,
          accessories: accessoriesValue,
          personalMobile: personalMobileValue,
          comments: values.remarks,
          fileName: currentFileUpload.filepath || undefined,
          empNumber: chosenUser.empNumber,
          username: usernameValue,
        } as ITRequestFormRequest;

        if (requisitionInfo?.requisitionNo) {
          body = {
            ...body,
            requestId: Number(requisitionInfo?.requisitionNo),
          };
        }

        setDisableSaveForm(true);

        try {
          const { requestId, createdDate, statusId } =
            await postITRequestForm(body).unwrap();

          dispatch(
            itRequisitionActions.setITRequisition({
              createdDate,
              requestId,
              reqStatusId: statusId,
            })
          );

          await submitForm(String(requestId)).unwrap();

          if (values.itReqType === ItRequisitionTypeCodes.EMAIL) {
            await uploadFile(String(requestId));
          }

          submitSuccessNotification(String(requestId));

          setDisableSaveForm(false);

          handleBackToListRequests();
        } catch (error) {
          const message = (error as RequestError)?.data;
          errorNotification(message);

          setDisableSaveForm(false);
        }
      };

      if (
        values.itReqType === ItRequisitionTypeCodes.EMAIL &&
        !isModalAccepted
      ) {
        setModalContext('submit');
        setIsModalOpened(true);
        return;
      }

      submitFormHandler();
    },
    [
      currentFileUpload,
      chosenUser,
      uploadFile,
      handleBackToListRequests,
      requisitionInfo,
      itAccessories,
      projects,
    ]
  );

  const validationSchema = useMemo(() => {
    const isFileAttached = !!currentFileUpload.filepath;
    const output = getItRequisitionFormSchema(itReqType, isFileAttached);
    return output;
  }, [itReqType, currentFileUpload]);

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: handleSubmitForm,
  });

  const changeItReqType = useCallback((value: number) => {
    formik.setFieldValue('itReqType', value);
    setItReqType(value);
  }, []);

  const validateCug = useCallback(
    (type: ItRequisitionTypeCodes) => {
      validateCugMobile(chosenUser.empNumber)
        .unwrap()
        .catch(() => {
          const message = getValidationCugError(type);
          errorNotification(message);

          changeItReqType(ItRequisitionTypeCodes.NEW_SIM_CARD);
        });
    },
    [chosenUser, changeItReqType]
  );

  const onItReqTypeChanged = useCallback(
    (value: number) => {
      setItReqType(value);

      const phone = isMobileNumberValid(chosenUser.mobile)
        ? chosenUser.mobile!
        : '';

      // on changing it requisition type it's need to clear all fields except chosen it requisition type and default values
      setInitialFormValues({
        ...initialValuesForm,
        contactNo: phone,
        itReqType: value,
        simCardCreditLimit: SimCapCodes.ZERO_DEFAULT,
      });

      if (value === ItRequisitionTypeCodes.SIM_ADDON_REQUEST) {
        validateCug(ItRequisitionTypeCodes.SIM_ADDON_REQUEST);
      }

      if (value === ItRequisitionTypeCodes.LOST_SIM_CARD) {
        validateCug(ItRequisitionTypeCodes.LOST_SIM_CARD);
      }
    },
    [validateCug, chosenUser]
  );

  const handleModuleChange = useCallback((value: string[]) => {
    if (!value.includes(String(ModuleOptions.PURCHASE))) {
      formik.setFieldValue('requiredRole', null);
    }

    if (!value.includes(String(ModuleOptions.ACCOUNTS))) {
      formik.setFieldValue('accountsModule', null);
    }
  }, []);

  useEffect(() => {
    const { value: itReqTypeValue } = formik.getFieldMeta('itReqType');

    if (itReqTypeValue === ItRequisitionTypeCodes.SIM_ADDON_REQUEST) {
      // revalidate cug if selected user was changed
      validateCug(ItRequisitionTypeCodes.SIM_ADDON_REQUEST);
    }

    if (itReqTypeValue === ItRequisitionTypeCodes.LOST_SIM_CARD) {
      // revalidate cug if selected user was changed
      validateCug(ItRequisitionTypeCodes.LOST_SIM_CARD);
    }
  }, [validateCug, chosenUser]);

  useEffect(() => {
    const { organization } = formik.values;

    if (organization) {
      getProjects(organization)
        .unwrap()
        .then((projectsResp) => {
          const transformedProjects = transformProjectsToOptions(projectsResp);
          setProjects(transformedProjects);
        })
        .catch(() => {
          setProjects([]);
        });
    }
  }, [formik.values.organization]);

  useEffect(() => {
    // setting value to trigger validation
    const timeStamp = dayjs().toISOString();
    formik.setFieldValue('document', timeStamp);
  }, [currentFileUpload.filepath]);

  const handleSaveAsDraft = useCallback(
    (isModalAccepted = false): void => {
      const saveAsDraft = async (): Promise<void> => {
        const intCallNumber = `+${formik.values.countryCode} ${formik.values.intCallsNumber}`;
        const personalMobileValue = getPersonalMobileValue(
          formik.values.itReqType,
          intCallNumber,
          formik.values.contactNo
        );

        const usernameValue = getUsernameValue(
          formik.values.itReqType,
          formik.values.emailIdLeft,
          formik.values.emailIdRight,
          chosenUser.empName
        );

        const accessoriesValue = getAccessoriesValue(
          formik.values.itReqType,
          formik.values.emailSignatureLeft,
          formik.values.emailSignatureRight,
          formik.values.accessories,
          itAccessories
        );

        const body = {
          reqItemId: formik.values.itReqType as number,
          reqCapValue: formik.values.simCardCreditLimit as number,
          isNewUser: formik.values.isNewUser as number,
          modules: formik.values.module,
          acctModules: formik.values.accountsModule,
          organizations: [formik.values.organization || ''],
          projects: formik.values.projects,
          role: formik.values.requiredRole,
          accessories: accessoriesValue,
          personalMobile: personalMobileValue,
          comments: formik.values.remarks,
          fileName: currentFileUpload.filepath || undefined,
          empNumber: chosenUser.empNumber,
          username: usernameValue,
        };

        setDisableSaveForm(true);

        try {
          const { requestId, createdDate, statusId } =
            await postITRequestForm(body).unwrap();

          dispatch(
            itRequisitionActions.setITRequisition({
              createdDate,
              requestId,
              reqStatusId: statusId,
            })
          );

          if (formik.values.itReqType === ItRequisitionTypeCodes.EMAIL) {
            await uploadFile(String(requestId));
          }

          saveSuccessNotification(String(requestId));

          setDisableSaveForm(false);

          handleBackToListRequests();
        } catch (error) {
          const message = (error as RequestError)?.data;
          errorNotification(message);

          setDisableSaveForm(false);
        }
      };

      if (
        formik.values.itReqType === ItRequisitionTypeCodes.EMAIL &&
        !isModalAccepted
      ) {
        setModalContext('save');
        setIsModalOpened(true);
        return;
      }

      const saveAsDraftHandler = async (): Promise<void> => {
        const isFormValid = await validateForm(validationSchema, formik);

        if (!isFormValid) {
          return;
        }

        await saveAsDraft();
      };

      saveAsDraftHandler();
    },
    [
      formik,
      currentFileUpload,
      chosenUser,
      handleBackToListRequests,
      uploadFile,
      validationSchema,
      itAccessories,
      projects,
    ]
  );

  const onModalCancel = (): void => {
    setIsModalOpened(false);
  };

  const onModalSubmit = (): void => {
    setIsModalOpened(false);

    const isModalAccepted = true;

    if (modalContext === 'save') {
      handleSaveAsDraft(isModalAccepted);
    } else if (modalContext === 'submit') {
      handleSubmitForm(formik.values, null, isModalAccepted);
    }
  };

  useEffect(() => {
    if (chosenUser) {
      const splittedName = splitUserName(chosenUser.empName);
      setSplittedUserName(splittedName);

      formik.setFieldValue('contactNo', chosenUser.mobile || '');
    }
  }, [chosenUser]);

  const handleSearchEmpByNumber = useCallback(
    (selectedEmpNumber?: string): void => {
      const validEmpNumber = selectedEmpNumber || currentUser.empNumber;

      if (!validEmpNumber) {
        return;
      }

      getUser({ empNo: validEmpNumber })
        .unwrap()
        .then((user) => setSelectedEmpUser(user));
    },
    [currentUser]
  );

  const handleApplySelectedUser = useCallback(() => {
    if (selectedEmpUser) {
      dispatch(userInfoActions.setSelectedUser(selectedEmpUser));
      // reset form after new user selected
      formik.setFieldValue('emailIdLeft', initialValuesForm.emailIdLeft);
      formik.setFieldValue('emailIdRight', initialValuesForm.emailIdRight);
      formik.setFieldValue(
        'emailSignatureLeft',
        initialValuesForm.emailSignatureLeft
      );
      formik.setFieldValue(
        'emailSignatureRight',
        initialValuesForm.emailSignatureRight
      );
      formik.setFieldValue(
        'emailSignatureRight',
        initialValuesForm.emailSignatureRight
      );
    }
  }, [dispatch, formik, selectedEmpUser]);

  const simCapValuesOptions = useMemo(() => {
    return transformDictToOptionsNum(simCapValues);
  }, [simCapValues]);

  const itAccessoriesOptions = useMemo(() => {
    return transformDictToOptions(itAccessories);
  }, [itAccessories]);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSaveAsDraft();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSaveAsDraft]
  );

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={chosenUser}
        selectedUser={selectedEmpUser}
        requisitionInfo={requisitionInfo}
        descriptionAlert={descriptionAlert}
        onFooterAction={handleFooterAction}
        handleSearchEmpByNumber={handleSearchEmpByNumber}
        handleApplySelectedUser={handleApplySelectedUser}
        showSelectEmployee
        disabledSaveForm={disabledSaveForm}
        formType="create"
      >
        <FormItems>
          <RequestDetailsSection
            simCapValues={simCapValuesOptions}
            itAccessories={itAccessoriesOptions}
            requisitionItems={itReqTypeOptions}
            splittedUserName={splittedUserName}
            onFileRequest={handleFileRequest}
            onFileRemove={handleFileRemove}
            label="Request Details"
            formik={formik}
            onItReqTypeChange={onItReqTypeChanged}
            organizations={organizations}
            projects={projects}
            onModuleChange={handleModuleChange}
            isModalOpened={isModalOpened}
            onModalCancel={onModalCancel}
            onModalSubmit={onModalSubmit}
          />
        </FormItems>
      </MainForm>
    </Flex>
  );
};

export default ITRequestCreateForm;
